import { useEffect, useState } from "react";
import { Activity, Info, LogIn, Menu, TrendingUp, UserPlus, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import "../navBar/TubelightNavbar.css";
import longLogo from "../../../images/imageNewLogo.webp";
import chartThree from "../../../images/Chart 3.webp";

const navItems = [
  {
    name: "Price Action Trader Bootcamp",
    url: "/priceActionBootcamp",
    icon: TrendingUp,
    featured: true,
  },
  {
    name: "Trade Floor",
    url: "/tradeFloor",
    icon: Activity,
  },
  {
    name: "About Us",
    url: "/about",
    icon: Info,
  },
];

const accountItems = [
  {
    name: "Log In",
    url: "https://andus.co.za/trade-login",
    icon: LogIn,
    candle: "red",
  },
  {
    name: "Register",
    url: "https://andus.co.za/trade-register",
    icon: UserPlus,
    primary: true,
    candle: "green",
  },
];

const SIGNUP_QUIZ_STORAGE_KEY = "tpc-signup-quiz-completed";

const signupQuizQuestions = [
  {
    id: "goal",
    prompt: "Your trading adventure begins here. What are you chasing first?",
    options: [
      "Build a solid skill I can trust",
      "Create a disciplined side-income path",
      "Train toward professional trading",
    ],
  },
  {
    id: "style",
    prompt: "Pick your learning vibe:",
    options: [
      "Step-by-step with simple structure",
      "Live practice and direct feedback",
      "Fast challenge mode with accountability",
    ],
  },
  {
    id: "time",
    prompt: "How much weekly time can you commit right now?",
    options: ["2-4 focused hours", "5-7 serious hours", "8+ all-in hours"],
  },
];

function isExternalUrl(url = "") {
  return /^https?:\/\//i.test(url);
}

function normalizeHref(url = "/") {
  if (!url) return "/";

  if (
    isExternalUrl(url) ||
    url.startsWith("mailto:") ||
    url.startsWith("tel:") ||
    url.startsWith("#")
  ) {
    return url;
  }

  return url.startsWith("/") ? url : `/${url}`;
}

function isPlainNavigationClick(event) {
  const target = event.currentTarget?.getAttribute("target");

  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    (!target || target === "_self")
  );
}

function scrollPageToTop() {
  if (typeof window === "undefined") return;

  try {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  } catch {
    window.scrollTo(0, 0);
  }
}

function handleNavigationClick(event, url = "/", onClick) {
  if (!isPlainNavigationClick(event)) return;

  onClick?.(event);

  if (event.defaultPrevented) return;

  scrollPageToTop();

  if (typeof window === "undefined") return;

  if (
    isExternalUrl(url) ||
    url.startsWith("mailto:") ||
    url.startsWith("tel:")
  ) {
    return;
  }

  event.preventDefault();

  const href = normalizeHref(url);
  const destination = new URL(href, window.location.origin);

  if (destination.href === window.location.href) {
    window.location.reload();
    return;
  }

  window.location.assign(destination.href);
}

function openExternalAuthUrl(url) {
  if (typeof window === "undefined" || !url) return;

  window.open(url, "_blank", "noopener,noreferrer");
}

function SmartLink({ item, className = "", children, onClick, ariaCurrent }) {
  const href = normalizeHref(item.url);
  const external = isExternalUrl(item.url);

  return (
    <a
      href={href}
      className={className}
      data-candle={item.candle || undefined}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      onClick={(event) => handleNavigationClick(event, item.url, onClick)}
      aria-current={ariaCurrent}
    >
      {children}
    </a>
  );
}

export default function TubelightNavbar({ className = "" }) {
  const location = useLocation();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [isSignupQuizOpen, setIsSignupQuizOpen] = useState(false);
  const [signupQuizStep, setSignupQuizStep] = useState(0);
  const [pendingSignupUrl, setPendingSignupUrl] = useState(
    "https://andus.co.za/trade-register",
  );

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    scrollPageToTop();
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !("scrollRestoration" in window.history)
    ) {
      return;
    }

    const originalScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    return () => {
      window.history.scrollRestoration = originalScrollRestoration;
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined" || !isMobileMenuOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen || typeof window === "undefined") return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollingDown =
        currentScrollY > lastScrollY && currentScrollY > 140;

      setIsScrollingDown(scrollingDown);
      lastScrollY = Math.max(currentScrollY, 0);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const closeSignupQuiz = () => {
    setIsSignupQuizOpen(false);
    setSignupQuizStep(0);
  };

  const launchSignupFlow = (url = "https://andus.co.za/trade-register") => {
    if (typeof window === "undefined") return;

    const hasCompletedQuiz =
      window.localStorage.getItem(SIGNUP_QUIZ_STORAGE_KEY) === "true";

    if (hasCompletedQuiz) {
      openExternalAuthUrl(url);
      return;
    }

    setPendingSignupUrl(url);
    setSignupQuizStep(0);
    setIsSignupQuizOpen(true);
  };

  const handleSignupQuizSelect = () => {
    const isLastStep = signupQuizStep >= signupQuizQuestions.length - 1;

    if (!isLastStep) {
      setSignupQuizStep((step) => step + 1);
      return;
    }

    if (typeof window !== "undefined") {
      window.localStorage.setItem(SIGNUP_QUIZ_STORAGE_KEY, "true");
      openExternalAuthUrl(pendingSignupUrl);
    }
  };

  const currentQuizQuestion = signupQuizQuestions[signupQuizStep];
  const quizProgress = ((signupQuizStep + 1) / signupQuizQuestions.length) * 100;

  const isItemActive = (item) => {
    if (isExternalUrl(item.url)) return false;

    const itemPath = normalizeHref(item.url).split(/[?#]/)[0];

    return (
      location.pathname === itemPath ||
      (itemPath !== "/" && location.pathname.startsWith(`${itemPath}/`))
    );
  };

  return (
    <header
      className={`tubelight-navbar-fixed ${
        isScrollingDown && !isMobileMenuOpen ? "navbar-hidden" : ""
      } ${className}`}
    >
      <nav className="tubelight-navbar-inner" aria-label="Primary navigation">
        <a
          href="/"
          className="tubelight-navbar-brand tubelight-navbar-brand-link"
          onClick={(event) => handleNavigationClick(event, "/", closeMobileMenu)}
          aria-label="Go to Trade Performance Centre home"
        >
          <span className="tubelight-navbar-logo-frame" aria-hidden="true">
            <img
              className="tubelight-navbar-logo-image"
              src={longLogo}
              alt=""
              decoding="async"
            />
          </span>

          <span className="tubelight-navbar-wordmark">
            <span className="tubelight-navbar-chart-bg" aria-hidden="true">
              <img src={chartThree} alt="" decoding="async" />
            </span>

            <span className="tubelight-navbar-brand-name">
              <span className="tubelight-navbar-brand-highlight">Trade</span>{" "}
              Performance Centre
            </span>
          </span>
        </a>

        <div className="tubelight-navbar-links" aria-label="Main pages">
          {navItems.map((item) => {
            const isActive = isItemActive(item);

            return (
              <SmartLink
                key={item.name}
                item={item}
                className={`tubelight-navbar-link ${
                  item.featured ? "tubelight-navbar-link-featured" : ""
                } ${isActive ? "active" : ""}`}
                ariaCurrent={isActive ? "page" : undefined}
              >
                {item.name}
              </SmartLink>
            );
          })}
        </div>

        <div className="tubelight-navbar-actions" aria-label="Account actions">
          {accountItems.map((item) => (
            <SmartLink
              key={item.name}
              item={item}
              onClick={
                item.name === "Register"
                  ? (event) => {
                      event.preventDefault();
                      launchSignupFlow(item.url);
                    }
                  : undefined
              }
              className={`tubelight-navbar-action ${
                item.primary
                  ? "tubelight-navbar-action-primary"
                  : "tubelight-navbar-action-secondary"
              }`}
            >
              <span>{item.name}</span>
            </SmartLink>
          ))}
        </div>

        <div className="tubelight-navbar-mobile-menu">
          <button
            type="button"
            className="mobile-menu-button"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div
          className="tpc-mobile-panels"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
        >
          <button
            type="button"
            className="tpc-mobile-backdrop"
            onClick={closeMobileMenu}
            aria-label="Close menu"
          />

          <div className="tpc-mobile-panel">
            <div className="tpc-mobile-panel-header">
              <a
                href="/"
                className="tpc-mobile-panel-brand tpc-mobile-panel-brand-link"
                onClick={(event) =>
                  handleNavigationClick(event, "/", closeMobileMenu)
                }
                aria-label="Go to Trade Performance Centre home"
              >
                <span className="tpc-mobile-panel-logo-frame" aria-hidden="true">
                  <img
                    className="tpc-mobile-panel-logo-image"
                    src={longLogo}
                    alt=""
                    decoding="async"
                  />
                </span>

                <span className="tpc-mobile-panel-wordmark">
                  <span className="tpc-mobile-panel-chart-bg" aria-hidden="true">
                    <img src={chartThree} alt="" decoding="async" />
                  </span>

                  <span className="tpc-mobile-panel-brand-name">
                    <span>Trade</span> Performance Centre
                  </span>
                </span>
              </a>

              <button
                type="button"
                className="tpc-mobile-panel-close"
                onClick={closeMobileMenu}
                aria-label="Close mobile menu"
              >
                <X size={17} />
              </button>
            </div>

            <div className="tpc-mobile-panel-section">
              <span className="tpc-mobile-panel-eyebrow">Navigation</span>

              <div className="tpc-mobile-panel-list">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = isItemActive(item);

                  return (
                    <SmartLink
                      key={item.name}
                      item={item}
                      onClick={closeMobileMenu}
                      className={`tpc-mobile-panel-item ${
                        item.featured ? "featured" : ""
                      } ${isActive ? "active" : ""}`}
                      ariaCurrent={isActive ? "page" : undefined}
                    >
                      <span className="tpc-mobile-panel-item-icon">
                        <Icon size={17} strokeWidth={2.4} />
                      </span>

                      <span className="tpc-mobile-panel-item-label">
                        {item.name}
                      </span>
                    </SmartLink>
                  );
                })}
              </div>
            </div>

            <div className="tpc-mobile-panel-actions">
              {accountItems.map((item) => {
                const Icon = item.icon;

                return (
                  <SmartLink
                    key={item.name}
                    item={item}
                    onClick={
                      item.name === "Register"
                        ? (event) => {
                            closeMobileMenu();
                            event.preventDefault();
                            launchSignupFlow(item.url);
                          }
                        : closeMobileMenu
                    }
                    className={`tpc-mobile-panel-action ${
                      item.primary
                        ? "tpc-mobile-panel-action-primary"
                        : "tpc-mobile-panel-action-secondary"
                    }`}
                  >
                    <Icon size={17} strokeWidth={2.4} />
                    <span>{item.name}</span>
                  </SmartLink>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {isSignupQuizOpen && currentQuizQuestion && (
        <div
          className="tpc-signup-quiz-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tpc-signup-quiz-title"
        >
          <button
            type="button"
            className="tpc-signup-quiz-backdrop"
            onClick={closeSignupQuiz}
            aria-label="Close signup quiz"
          />

          <div className="tpc-signup-quiz-card">
            <button
              type="button"
              className="tpc-signup-quiz-close"
              onClick={closeSignupQuiz}
              aria-label="Close signup quiz"
            >
              <X size={16} />
            </button>

            <p className="tpc-signup-quiz-kicker">Quick adventure match</p>
            <h3 id="tpc-signup-quiz-title">Let us find your best path</h3>

            <div className="tpc-signup-quiz-progressTrack" aria-hidden="true">
              <span
                className="tpc-signup-quiz-progressFill"
                style={{ width: `${quizProgress}%` }}
              />
            </div>

            <p className="tpc-signup-quiz-stepLabel">
              Question {signupQuizStep + 1} of {signupQuizQuestions.length}
            </p>
            <p className="tpc-signup-quiz-question">{currentQuizQuestion.prompt}</p>

            <div className="tpc-signup-quiz-options">
              {currentQuizQuestion.options.map((option) => (
                <button
                  key={option}
                  type="button"
                  className="tpc-signup-quiz-option"
                  onClick={handleSignupQuizSelect}
                >
                  {option}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="tpc-signup-quiz-skip"
              onClick={() => {
                if (typeof window !== "undefined") {
                  openExternalAuthUrl(pendingSignupUrl);
                }
              }}
            >
              Skip for now
            </button>
          </div>
        </div>
      )}
    </header>
  );
}